import {
	CalendarWeekOutput,
	DateFormat,
	ISOStringToUserDate,
	isStringValidDate,
	userDateToISO,
	userInputStringToDate,
} from '@agilox/ui-common';
import {
	Component,
	effect,
	input,
	model,
	output,
	signal,
	untracked,
	viewChild,
} from '@angular/core';
import { FormValueControl } from '@angular/forms/signals';
import { DropdownDirective } from '../../dropdown/directives';

@Component({
	selector: 'ui-datepicker-input',
	templateUrl: './datepicker-input.component.html',
	standalone: false,
})
export class DatepickerInputComponent implements FormValueControl<string | null> {
	hasError = model<boolean>(false);
	inputId = input<string>('');
	invalid = input<boolean>(false);
	syncedDate = input<Date | undefined>(undefined);
	showWhenValid = input<boolean>(false);
	disabled = input<boolean>(false);
	fullWidth = input<boolean>(false);
	minDate = input<Date | undefined>(undefined);
	maxDate = input<Date | undefined>(undefined);
	userFormat = input.required<DateFormat>();
	excludedDates = input<Date[]>([]);
	currentlyDisplayedMonth = model<Date>(new Date());
	placeholder = input<string | undefined>('');
	useContainerWidth = input<boolean>(false);
	containerSelector = input<string>('.app');

	value = model<string | null>(null);

	iconClick = output<void>();
	touch = output<void>();
	calendarWeekClicked = output<CalendarWeekOutput>();
	calendarOpened = output<void>();

	input = viewChild<HTMLInputElement>('input');
	dropdown = viewChild(DropdownDirective);

	/** The masked text the user sees, in their own date format. */
	displayValue = signal<string>('');

	constructor() {
		effect(() => {
			const value = this.value();
			const format = this.userFormat();
			untracked(() => this.showValue(value, format));
		});
	}

	focused = signal(false);
	dropdownOpened = signal(false);

	onInput(event: Event): void {
		const element = event.target as HTMLInputElement;
		this.commit(element.value);

		if (element.value !== this.displayValue()) {
			element.value = this.displayValue();
		}
	}

	private commit(raw: string | null): void {
		const masked = this.doMask(raw);
		this.displayValue.set(masked);
		this.setErrors(masked);

		if (this.isValid(masked)) {
			const isoDate: string = userDateToISO(masked, this.userFormat());
			this.currentlyDisplayedMonth.set(new Date(isoDate));
			this.value.set(isoDate);
			this.touch.emit();
		} else {
			this.value.set(null);
		}
	}

	private showValue(value: string | null, format: DateFormat): void {
		if (this.focused()) {
			return;
		}

		this.displayValue.set(value ? ISOStringToUserDate(value, format) : '');
	}

	private isValid(value: string) {
		return isStringValidDate(value, this.userFormat()) ? this.isBetweenMinMax(value) : false;
	}

	private setErrors(value: string | null) {
		this.hasError.set(!!value && !this.isValid(value));
	}

	private isBetweenMinMax(date: string): boolean {
		const minDate = this.minDate();
		const maxDate = this.maxDate();

		const largerThanMin: boolean = minDate
			? userInputStringToDate(date, this.userFormat()) >= minDate
			: true;
		const smallerThanMax: boolean = maxDate
			? userInputStringToDate(date, this.userFormat()) <= maxDate
			: true;
		return largerThanMin && smallerThanMax;
	}

	/**
	 * Mask the input value
	 * e.g. input: 1234567890 -> output: 12.34.5678
	 *
	 *
	 * @param value
	 * @private
	 */
	private doMask(value: string | null): string {
		if (!value) {
			return '';
		}

		/**
		 * If the value already has two dots, then the user is
		 * trying to delete a character, so we need to adjust the cursor position
		 * and not add the dots again!
		 */
		const literal = this.userFormat().literal;
		const allowed = value.replace(new RegExp(`[^\\d\\${literal}]`, 'g'), '');
		if (allowed.match(new RegExp(`\\${literal}`, 'g'))?.length === 2) {
			return allowed;
		}

		// remove all non digit characters
		const sanitized = allowed.replace(/[^\d]/g, '');

		const literalPositions: number[] = [2, 4];
		let result: string = '';
		for (let i = 0; i < sanitized.length; i++) {
			if (literalPositions.includes(i) && !(i > 0 && sanitized[i - 1] === literal)) {
				result += literal;
			}
			result += sanitized[i];
		}
		return result.slice(0, 8);
	}

	calendarSelection(date: string) {
		this.commit(ISOStringToUserDate(date, this.userFormat()));
		this.dropdown()?.closeDropdown();
	}

	navDateChange(date: Date) {
		this.currentlyDisplayedMonth.set(date);
	}

	openCalendar() {
		/**
		 * This mitigates the issue where the dropdown is not opened
		 * if the calendar is opened immediately after being closed.
		 * We wait for the next tick to open the dropdown.
		 */

		if (!this.dropdownOpened()) {
			setTimeout(() => {
				this.dropdown()?.openDropdown();
				this.calendarOpened.emit();
			});
		}
	}

	closeCalendar() {
		this.dropdown()?.closeDropdown();
	}

	onDropdownStateChange(opened: boolean) {
		this.dropdownOpened.set(opened);
	}
}
